import React, { useEffect, useState } from 'react';

import { AvailabilityBlock, getMyAvailability, replaceMyAvailability } from '../api/availability';

const DAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 0, label: 'Domingo' },
];

const TIMEZONES = [
  'America/Mexico_City',
  'America/Bogota',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const DEFAULT_TZ = 'America/Mexico_City';

/** A recurring weekly block as edited in the UI. */
interface RecurringRow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

/**
 * PI-2 Epic 3 (S3.1) — teacher availability authoring.
 *
 * Lets a verified teacher publish the weekly recurring windows that the parent's
 * booking flow (SchedulePanel → available-windows) expands into bookable slots.
 *
 * One-off EXCEPTION blocks (TCH-13-FR-02) are edited here too. They were
 * supported by the domain and the API from the start and merely preserved by
 * this screen, which meant a teacher could publish a weekly schedule and then
 * had no way to say "not this Thursday" — the one thing a recurring schedule
 * cannot express.
 */
const TeacherAvailability: React.FC = () => {
  const [rows, setRows] = useState<RecurringRow[]>([]);
  const [exceptions, setExceptions] = useState<AvailabilityBlock[]>([]);
  const [timezone, setTimezone] = useState<string>(DEFAULT_TZ);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { availability } = await getMyAvailability();
        if (cancelled) return;
        const blocks = availability?.blocks ?? [];
        const recurring = blocks
          .filter((b) => b.kind === 'RECURRING')
          .map((b) => ({
            dayOfWeek: b.dayOfWeek ?? 1,
            startTime: b.startTime ?? '16:00',
            endTime: b.endTime ?? '19:00',
          }));
        setRows(recurring);
        setExceptions(blocks.filter((b) => b.kind === 'EXCEPTION'));
        const tz = blocks.find((b) => b.timezone)?.timezone;
        if (tz) setTimezone(tz);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'No se pudo cargar tu disponibilidad');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addRow = () => {
    setInfo(null);
    setRows((r) => [...r, { dayOfWeek: 1, startTime: '16:00', endTime: '19:00' }]);
  };

  const updateRow = (idx: number, patch: Partial<RecurringRow>) => {
    setInfo(null);
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  const removeRow = (idx: number) => {
    setInfo(null);
    setRows((r) => r.filter((_, i) => i !== idx));
  };

  /** Today in the teacher's own calendar, used as the floor for a new exception. */
  const today = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate(),
    ).padStart(2, '0')}`;
  };

  const addException = () => {
    setInfo(null);
    // Defaults to "unavailable", which is what an exception is for the vast
    // majority of the time: a day off inside an otherwise regular week.
    setExceptions((e) => [...e, { kind: 'EXCEPTION', date: today(), available: false, timezone }]);
  };

  const updateException = (idx: number, patch: Partial<AvailabilityBlock>) => {
    setInfo(null);
    setExceptions((e) => e.map((block, i) => (i === idx ? { ...block, ...patch } : block)));
  };

  const removeException = (idx: number) => {
    setInfo(null);
    setExceptions((e) => e.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setError(null);
    setInfo(null);

    for (const row of rows) {
      if (row.startTime >= row.endTime) {
        setError(
          `Revisa los horarios: la hora de inicio debe ser anterior a la de fin (${row.startTime} – ${row.endTime}).`,
        );
        return;
      }
    }

    // Overlap pre-check so the teacher sees which two rows collide instead of a
    // generic server rejection. The backend rule is still the authority.
    // Half-open comparison: back-to-back blocks (10:00-12:00, 12:00-14:00) are fine.
    for (let i = 0; i < rows.length; i++) {
      for (let j = i + 1; j < rows.length; j++) {
        const a = rows[i];
        const b = rows[j];
        if (a.dayOfWeek !== b.dayOfWeek) continue;
        if (a.startTime < b.endTime && b.startTime < a.endTime) {
          const day = DAYS.find((d) => d.value === a.dayOfWeek)?.label ?? '';
          setError(
            `Horarios encimados el ${day}: ${a.startTime}–${a.endTime} se cruza con ${b.startTime}–${b.endTime}. Ajusta o elimina uno de los dos.`,
          );
          return;
        }
      }
    }

    for (const block of exceptions) {
      if (!block.date) {
        setError('Cada excepción necesita una fecha.');
        return;
      }
      if (block.available) {
        if (!block.startTime || !block.endTime) {
          setError(
            `La excepción del ${block.date} abre un horario, así que necesita hora de inicio y de fin.`,
          );
          return;
        }
        if (block.startTime >= block.endTime) {
          setError(
            `Revisa la excepción del ${block.date}: la hora de inicio debe ser anterior a la de fin.`,
          );
          return;
        }
      }
    }

    // Mirrors the server rule (findOverlappingBlocks): only two *open* windows on
    // the same date can collide. "No disponible" plus "horario extra" on one date
    // is legitimate and common — it suppresses the weekly blocks and offers a
    // different window instead — so it must not be pre-rejected here.
    const open = exceptions.filter((b) => b.available && b.startTime && b.endTime);
    for (let i = 0; i < open.length; i++) {
      for (let j = i + 1; j < open.length; j++) {
        const a = open[i];
        const b = open[j];
        if (a.date !== b.date) continue;
        if (a.startTime! < b.endTime! && b.startTime! < a.endTime!) {
          setError(
            `Horarios encimados el ${a.date}: ${a.startTime}–${a.endTime} se cruza con ${b.startTime}–${b.endTime}.`,
          );
          return;
        }
      }
    }

    const recurringBlocks: AvailabilityBlock[] = rows.map((row) => ({
      kind: 'RECURRING',
      dayOfWeek: row.dayOfWeek,
      startTime: row.startTime,
      endTime: row.endTime,
      timezone,
    }));
    // Preserve one-off exception blocks (re-tagged with the current tz) so a save
    // doesn't wipe them — the backend PUT replaces the full set. Only DTO-allowed
    // fields are sent (no blockId); the API rejects unknown properties.
    const exceptionBlocks: AvailabilityBlock[] = exceptions.map((b) => ({
      kind: 'EXCEPTION',
      date: b.date,
      available: b.available,
      // A day marked unavailable carries no hours: sending leftovers from a
      // toggle would persist a window the teacher just said they cannot teach.
      ...(b.available ? { startTime: b.startTime, endTime: b.endTime } : {}),
      timezone,
    }));

    setSaving(true);
    try {
      await replaceMyAvailability([...recurringBlocks, ...exceptionBlocks]);
      setInfo('Disponibilidad guardada. Los padres ya pueden proponer horarios en estas ventanas.');
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar la disponibilidad');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Cargando tu disponibilidad...</p>;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1>Mi disponibilidad</h1>
      <p style={{ color: '#555', marginTop: 0 }}>
        Define tus ventanas semanales recurrentes. Los padres solo pueden agendar sesiones dentro de
        estos horarios.
      </p>

      {error && <div style={alertStyle('#ffe3e3', '#ff8787', '#c92a2a')}>{error}</div>}
      {info && <div style={alertStyle('#d3f9d8', '#51cf66', '#2b8a3e')}>{info}</div>}

      <section style={cardStyle}>
        <label style={{ fontSize: 13, color: '#666', display: 'block', marginBottom: 6 }}>
          Zona horaria
        </label>
        <select
          aria-label="Zona horaria"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          style={selectStyle}
        >
          {TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
      </section>

      <section style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Bloques semanales</h3>

        {rows.length === 0 && (
          <p style={{ color: '#666' }}>
            Aún no has agregado bloques. Agrega al menos uno para que aparezcan horarios
            disponibles.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((row, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <select
                aria-label="Día de la semana"
                value={row.dayOfWeek}
                onChange={(e) => updateRow(idx, { dayOfWeek: Number(e.target.value) })}
                style={{ ...selectStyle, width: 'auto', flex: '1 1 140px' }}
              >
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              <input
                type="time"
                aria-label="Hora de inicio del bloque"
                value={row.startTime}
                onChange={(e) => updateRow(idx, { startTime: e.target.value })}
                style={{ ...inputStyle, width: 'auto' }}
              />
              <span style={{ color: '#666' }}>—</span>
              <input
                type="time"
                aria-label="Hora de fin del bloque"
                value={row.endTime}
                onChange={(e) => updateRow(idx, { endTime: e.target.value })}
                style={{ ...inputStyle, width: 'auto' }}
              />
              <button onClick={() => removeRow(idx)} style={removeBtnStyle} aria-label="Eliminar">
                Eliminar
              </button>
            </div>
          ))}
        </div>

        <button onClick={addRow} style={ghostBtnStyle}>
          + Agregar bloque
        </button>
      </section>

      <section style={cardStyle}>
        <h3 style={{ marginTop: 0 }}>Excepciones</h3>
        <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>
          Días sueltos que no siguen tu semana habitual: un día que no puedes dar clase, o un
          horario extra que sí puedes. Una excepción manda sobre los bloques semanales de ese día.
        </p>

        {exceptions.length === 0 && (
          <p style={{ color: '#666' }}>
            Sin excepciones. Tu semana habitual aplica todos los días.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {exceptions.map((block, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <input
                type="date"
                aria-label="Fecha de la excepción"
                value={block.date ?? ''}
                onChange={(e) => updateException(idx, { date: e.target.value })}
                style={{ ...inputStyle, width: 'auto' }}
              />
              <select
                aria-label="Tipo de excepción"
                value={block.available ? 'available' : 'unavailable'}
                onChange={(e) => {
                  const available = e.target.value === 'available';
                  // The time inputs show 16:00–19:00 as their default. Those are
                  // display values until they are in state, and a save built
                  // from empty state is rejected for missing hours the teacher
                  // can see on screen.
                  updateException(
                    idx,
                    available
                      ? {
                          available,
                          startTime: block.startTime ?? '16:00',
                          endTime: block.endTime ?? '19:00',
                        }
                      : { available },
                  );
                }}
                style={{ ...selectStyle, width: 'auto', flex: '1 1 180px' }}
              >
                <option value="unavailable">No disponible</option>
                <option value="available">Horario extra</option>
              </select>
              {block.available && (
                <>
                  <input
                    type="time"
                    aria-label="Hora de inicio"
                    value={block.startTime ?? '16:00'}
                    onChange={(e) => updateException(idx, { startTime: e.target.value })}
                    style={{ ...inputStyle, width: 'auto' }}
                  />
                  <span style={{ color: '#666' }}>—</span>
                  <input
                    type="time"
                    aria-label="Hora de fin"
                    value={block.endTime ?? '19:00'}
                    onChange={(e) => updateException(idx, { endTime: e.target.value })}
                    style={{ ...inputStyle, width: 'auto' }}
                  />
                </>
              )}
              <button
                onClick={() => removeException(idx)}
                style={removeBtnStyle}
                aria-label="Eliminar excepción"
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>

        <button onClick={addException} style={ghostBtnStyle}>
          + Agregar excepción
        </button>
      </section>

      <button onClick={save} disabled={saving} style={primaryButtonStyle(saving)}>
        {saving ? 'Guardando...' : 'Guardar disponibilidad'}
      </button>
    </div>
  );
};

const alertStyle = (bg: string, border: string, color: string): React.CSSProperties => ({
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 8,
  padding: 12,
  margin: '12px 0',
  color,
});

const cardStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
};

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid #ced4da',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  width: '100%',
};

const removeBtnStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #c92a2a',
  borderRadius: 4,
  background: '#fff',
  color: '#c92a2a',
  fontSize: 13,
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '8px 14px',
  border: '1px solid #1971c2',
  borderRadius: 4,
  background: '#fff',
  color: '#1971c2',
  fontSize: 13,
  cursor: 'pointer',
};

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: 14,
  backgroundColor: disabled ? '#a0c4e8' : '#1971c2',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 16,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
});

export default TeacherAvailability;
